import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { AuthShell, PrimaryButton, OutlineButton, AUTH_SUBTLE } from './authShared';

function VerifyEmail({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [errorMessage, setErrorMessage] = useState('');

  const token = searchParams.get('token');

  const verifyEmailToken = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided');
      return;
    }
    try {
      const response = await apiService.verifyEmail(token);
      setStatus('success');
      if (response.data?.user) {
        setTimeout(() => {
          localStorage.setItem('user', JSON.stringify(response.data.user));
          onLogin(response.data.user);
          toast.success('Email verified! Welcome to DrawReady!');
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Verification failed';
      if (detail.toLowerCase().includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('error');
      }
      setErrorMessage(detail);
    }
  }, [token, navigate, onLogin]);

  useEffect(() => {
    verifyEmailToken();
  }, [verifyEmailToken]);

  const Dots = ({ color }) => (
    <div className="animate-pulse flex justify-center gap-2 mt-4">
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
      <div className="w-2 h-2 rounded-full" style={{ background: color }} />
    </div>
  );

  return (
    <AuthShell backTo={null}>
      {status === 'verifying' && (
        <div className="text-center">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#EEF2FB' }}>
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#1E4ED8' }} />
          </div>
          <h1 className="text-2xl">Verifying your email</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Please wait while we verify your email address...</p>
          <Dots color="#93B4FF" />
        </div>
      )}

      {status === 'success' && (
        <div className="text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: '#059669' }} />
          <h1 className="text-2xl">Email verified</h1>
          <p className="mt-2 text-[15px]" style={{ color: AUTH_SUBTLE }}>Your email has been successfully verified. Redirecting you to the dashboard...</p>
          <Dots color="#6EE7B7" />
        </div>
      )}

      {status === 'error' && (
        <div className="text-center">
          <XCircle className="w-14 h-14 mx-auto mb-4" style={{ color: '#DC2626' }} />
          <h1 className="text-2xl">Verification failed</h1>
          <p className="mt-2 mb-6 text-[15px]" style={{ color: AUTH_SUBTLE }}>{errorMessage || 'The verification link may be invalid or has already been used. Please try logging in or request a new verification email.'}</p>
          <div className="space-y-3">
            <PrimaryButton type="button" onClick={() => navigate('/login')}>Go to Login</PrimaryButton>
            <OutlineButton type="button" onClick={() => navigate('/signup')}>Create New Account</OutlineButton>
          </div>
        </div>
      )}

      {status === 'expired' && (
        <div className="text-center">
          <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#FEF3E2' }}>
            <Mail className="w-6 h-6" style={{ color: '#B45309' }} />
          </div>
          <h1 className="text-2xl">Link expired</h1>
          <p className="mt-2 mb-6 text-[15px]" style={{ color: AUTH_SUBTLE }}>Verification links expire after 24 hours for security. Please log in to request a new verification email.</p>
          <PrimaryButton type="button" onClick={() => navigate('/login')}>Go to Login</PrimaryButton>
        </div>
      )}

      <div className="flex justify-center gap-5 mt-6 text-xs">
        <Link to="/" style={{ color: AUTH_SUBTLE }} className="hover:opacity-70">Home</Link>
        <Link to="/login" style={{ color: AUTH_SUBTLE }} className="hover:opacity-70">Login</Link>
        <Link to="/signup" style={{ color: AUTH_SUBTLE }} className="hover:opacity-70">Sign Up</Link>
      </div>
    </AuthShell>
  );
}

export default VerifyEmail;
